import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Paper,
  Alert
} from '@mui/material';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../index';
import { GameSession, TeamColor, Player } from '../types';
import { normalizeJapanese, checkAnswer } from '../utils/normalize';
import { getOwnAnswerId } from '../data/opAnswerMappings';
import { BATTLE_ROYALE_ANSWERS } from '../data/answers';
import { TEAM_COLORS } from '../constants/teamColors';

export const TutorialScreen: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [answer, setAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [assignedTeamColor, setAssignedTeamColor] = useState<TeamColor | null>(null);
  const [session, setSession] = useState<GameSession | null>(null);
  const [player, setPlayer] = useState<Player | null>(null);

  const playerId = sessionStorage.getItem('battleRoyalePlayerId');

  // デバッグ用
  useEffect(() => {
    console.log('TutorialScreen mounted', { sessionId, playerId });
  }, []);

  useEffect(() => {
    if (!sessionId || !playerId) {
      console.error('Missing sessionId or playerId', { sessionId, playerId });
      return;
    }

    // セッション情報とプレイヤー情報を取得
    const fetchData = async () => {
      const sessionDoc = await getDoc(doc(db, 'battleRoyale', sessionId));
      if (sessionDoc.exists()) {
        setSession({ id: sessionDoc.id, ...sessionDoc.data() } as GameSession);
      }

      const playerDoc = await getDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId));
      if (playerDoc.exists()) {
        setPlayer({ id: playerDoc.id, ...playerDoc.data() } as Player);
      }
    };

    fetchData();
  }, [sessionId, playerId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sessionId || !playerId || !session) {
      setError('セッション情報が見つかりません');
      return;
    }

    if (!answer.trim()) {
      setError('答えを入力してください');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // 答えが問題セットに含まれているかチェック
      let isValidAnswer = false;
      let assignedTeam = TeamColor.RED; // デフォルト
      
      // まず答えが有効かチェック
      let matchedAnswer = null;
      for (const answerData of BATTLE_ROYALE_ANSWERS) {
        if (checkAnswer(answer, answerData.canonical, answerData.synonyms)) {
          isValidAnswer = true;
          matchedAnswer = answerData;
          break;
        }
      }

      if (isValidAnswer && matchedAnswer) {
        // OPマッピングから、この答えに対応するチーム色を取得
        // matchedAnswerのすべての同義語をチェック
        const allVariations = [matchedAnswer.canonical, ...matchedAnswer.synonyms];
        
        for (const variation of allVariations) {
          if (session.settings.opAnswerMappings[variation]) {
            assignedTeam = session.settings.opAnswerMappings[variation] as TeamColor;
            break;
          }
        }
        
        console.log('Team assignment:', { 
          answer, 
          matchedAnswerId: matchedAnswer.id,
          assignedTeam 
        });
      }

      if (!isValidAnswer) {
        setError('不正解！');
        setAnswer(''); // 入力をクリア
        setLoading(false);
        return;
      }

      // 正解の場合
      setSuccess(true);
      setAssignedTeamColor(assignedTeam);
      
      // 答えIDを生成
      const ownAnswerId = getOwnAnswerId(answer);

      // プレイヤー情報を更新（チュートリアル正解で1ポイント付与）
      await updateDoc(doc(db, 'battleRoyale', sessionId, 'players', playerId), {
        teamColor: assignedTeam,
        ownAnswerId: ownAnswerId,
        answeredIds: [ownAnswerId], // チュートリアルで答えた問題IDを追加
        score: 1  // answeredIds.lengthと同じ
      });

      // 2秒後に本編画面へ遷移
      setTimeout(() => {
        navigate(`/battle-royale/player/${sessionId}/game`);
      }, 2000);
    } catch (err) {
      console.error('Tutorial submission error:', err);
      setError('エラーが発生しました。もう一度お試しください。');
      setLoading(false);
    }
  };

  // sessionIdが取得できていない場合のチェック
  if (!sessionId) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography color="error">セッションIDが見つかりません</Typography>
        </Box>
      </Container>
    );
  }

  // playerIdが取得できていない場合のチェック
  if (!playerId) {
    return (
      <Container maxWidth="sm">
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center">
            <Typography color="error" gutterBottom>プレイヤー登録が必要です</Typography>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/battle-royale/player/${sessionId}`)}
            >
              登録画面へ
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ px: 0 }}>
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between'
        }}
      >
        {/* ヘッダー */}
        <Box
          sx={{
            bgcolor: 'grey.700',
            color: 'white',
            py: 2,
            px: 3
          }}
        >
          <Typography variant="h6" align="center">
            あなたのテーブル番号 {player?.tableNumber || '---'}
          </Typography>
        </Box>

        {/* メインコンテンツ */}
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', px: 3 }}>
          <Paper elevation={0} sx={{ width: '100%', bgcolor: 'grey.100', p: 4 }}>
            <Typography
              variant="h5"
              align="center"
              sx={{ mb: 4, fontWeight: 'bold' }}
            >
              まずは自分の謎の答えを入力しよう
            </Typography>

            {error && (
              <Typography 
                variant="h5" 
                align="center" 
                sx={{ mb: 2, color: 'blue', fontWeight: 'bold' }}
              >
                {error}
              </Typography>
            )}

            {success && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography 
                  variant="h5" 
                  sx={{ color: 'red', fontWeight: 'bold' }}
                >
                  正解！
                </Typography>
              </Box>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="答えを入力"
                variant="outlined"
                sx={{
                  mb: 3,
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !answer.trim() || success}
                sx={{
                  py: 2,
                  bgcolor: 'grey.800',
                  '&:hover': {
                    bgcolor: 'grey.900'
                  }
                }}
              >
                送信
              </Button>
            </form>
          </Paper>
        </Box>

        {/* フッター */}
        <Box sx={{ bgcolor: 'grey.700', color: 'white', py: 2, px: 3 }}>
          {/* テキストなし、背景のみ */}
        </Box>
      </Box>
    </Container>
  );
};